import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Badge, ErrorText } from "../components/ui/feedback";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { Menu, type MenuItem } from "../components/ui/Menu";
import { PageHeader } from "../components/ui/PageHeader";
import { adminApi } from "./api";
import { OperatorEmailModal, SignInLinkModal } from "./OperatorDialogs";
import { DenseTable, QueryError, TD_CLASS, TH_CLASS } from "./dense";
import { ADMIN_KEY, adminOperatorsQuery } from "./queries";
import type { AdminOperator } from "./types";

type Dialog =
  | { kind: "email"; operator: AdminOperator }
  | { kind: "unlink"; operator: AdminOperator }
  | { kind: "signin"; operator: AdminOperator; url: string }
  | null;

const GOOGLE = "google";

/** Operator search and account actions: change email, unlink Google, issue a sign-in link. */
export function OperatorsPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState<Dialog>(null);
  const operators = useQuery(adminOperatorsQuery(q.trim()));

  const refresh = () => queryClient.invalidateQueries({ queryKey: [...ADMIN_KEY, "operators"] });

  const unlink = useMutation({
    mutationFn: (id: string) => adminApi.unlinkGoogle(id),
    onSuccess: () => {
      setDialog(null);
      return refresh();
    },
  });

  const signIn = useMutation({
    mutationFn: (operator: AdminOperator) => adminApi.createSignInLink(operator.id),
    onSuccess: (result, operator) => {
      setDialog({ kind: "signin", operator, url: result.url });
    },
  });

  const busy = unlink.isPending || signIn.isPending;
  const rows = operators.data ?? [];

  const menuFor = (operator: AdminOperator): MenuItem[] => {
    const items: (MenuItem | null)[] = [
      {
        label: t("admin.action.changeEmail"),
        disabled: busy,
        onSelect: () => setDialog({ kind: "email", operator }),
      },
      operator.identities.includes(GOOGLE)
        ? {
            label: t("admin.action.unlinkGoogle"),
            tone: "danger",
            disabled: busy,
            onSelect: () => setDialog({ kind: "unlink", operator }),
          }
        : null,
      {
        label: t("admin.action.signInLink"),
        disabled: busy,
        onSelect: () => signIn.mutate(operator),
      },
    ];
    return items.filter((item): item is MenuItem => item !== null);
  };

  return (
    <div className="space-y-5">
      <PageHeader title={t("admin.operators.title")} />
      <Input
        type="search"
        aria-label={t("admin.search")}
        placeholder={t("admin.search")}
        value={q}
        onChange={(event) => setQ(event.target.value)}
        className="md:max-w-64"
      />
      <ErrorText error={unlink.error ?? signIn.error} />
      {operators.isError ? (
        <QueryError error={operators.error} onRetry={() => void operators.refetch()} />
      ) : null}
      {operators.isPending ? <p className="text-operate text-ink-3">{t("app.loading")}</p> : null}
      {operators.data && rows.length === 0 ? <EmptyState>{t("admin.empty")}</EmptyState> : null}

      {rows.length > 0 ? (
        <DenseTable label={t("admin.operators.title")}>
          <thead>
            <tr>
              <th scope="col" className={TH_CLASS}>
                {t("admin.col.email")}
              </th>
              <th scope="col" className={TH_CLASS}>
                {t("admin.col.admin")}
              </th>
              <th scope="col" className={`${TH_CLASS} hidden md:table-cell`}>
                {t("admin.col.identities")}
              </th>
              <th scope="col" className={`${TH_CLASS} hidden md:table-cell`}>
                {t("admin.col.workspaces")}
              </th>
              <th scope="col" className={`${TH_CLASS} text-right`}>
                <span className="sr-only">{t("admin.col.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((operator) => (
              <tr key={operator.id} className="hover:bg-blue-wash/40">
                <td className={`${TD_CLASS} break-all`}>
                  <span className="block font-semibold">{operator.email}</span>
                  <span className="block text-micro text-ink-3">{operator.name}</span>
                </td>
                <td className={TD_CLASS}>
                  {operator.isAdmin ? (
                    <Badge tone="accent">{t("admin.yes")}</Badge>
                  ) : (
                    <span className="text-ink-3">{t("admin.no")}</span>
                  )}
                </td>
                <td className={`${TD_CLASS} hidden text-ink-2 md:table-cell`}>
                  {operator.identities.length > 0
                    ? operator.identities.join(", ")
                    : t("common.none")}
                </td>
                <td className={`${TD_CLASS} hidden text-ink-2 md:table-cell`}>
                  {operator.workspaces.length > 0
                    ? operator.workspaces.map((item) => `${item.name} (${item.role})`).join(", ")
                    : t("common.none")}
                </td>
                <td className={`${TD_CLASS} text-right`}>
                  <Menu
                    label={t("roster.moreActions", { name: operator.email })}
                    items={menuFor(operator)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </DenseTable>
      ) : null}

      {dialog?.kind === "email" ? (
        <OperatorEmailModal
          operator={dialog.operator}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            void refresh();
          }}
        />
      ) : null}
      {dialog?.kind === "signin" ? (
        <SignInLinkModal
          operator={dialog.operator}
          url={dialog.url}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "unlink" ? (
        <ConfirmDialog
          title={t("admin.unlink.title")}
          message={t("admin.unlink.message", { email: dialog.operator.email })}
          confirmLabel={t("admin.action.unlinkGoogle")}
          variant="danger"
          busy={unlink.isPending}
          error={unlink.error}
          onCancel={() => setDialog(null)}
          onConfirm={() => unlink.mutate(dialog.operator.id)}
        />
      ) : null}
    </div>
  );
}
